/* i18n bootstrap — loaded once from src/index.js before <App /> mounts.
 *
 * Scope (Minimum, Feb-2026): Landing hero + top-nav, Login sign-in card,
 * and TitleBar user menu across 6 languages: EN, ES, FR, DE, PT, ZH.
 * Admin panels stay English-only per product decision.
 *
 * Persistence order:
 *   1. authenticated user's saved language (applied in auth_context after /auth/me)
 *   2. localStorage `pw_lang`
 *   3. browser `navigator.language`
 *   4. fallback: 'en'
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import pt from './locales/pt.json';
import zh from './locales/zh.json';

const FLAG_API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api/landing/flags`;

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English',     nativeLabel: 'English',    flag: '🇬🇧', flagUrl: `${FLAG_API_ROOT}/en` },
  { code: 'es', label: 'Spanish',     nativeLabel: 'Español',    flag: '🇪🇸', flagUrl: `${FLAG_API_ROOT}/es` },
  { code: 'fr', label: 'French',      nativeLabel: 'Français',   flag: '🇫🇷', flagUrl: `${FLAG_API_ROOT}/fr` },
  { code: 'de', label: 'German',      nativeLabel: 'Deutsch',    flag: '🇩🇪', flagUrl: `${FLAG_API_ROOT}/de` },
  { code: 'pt', label: 'Portuguese',  nativeLabel: 'Português',  flag: '🇵🇹', flagUrl: `${FLAG_API_ROOT}/pt` },
  { code: 'zh', label: 'Chinese',     nativeLabel: '中文',        flag: '🇨🇳', flagUrl: `${FLAG_API_ROOT}/zh` },
];

export const LANGUAGE_STORAGE_KEY = 'pw_lang';
// Sep-2026 — set only when a human explicitly picks a language (LanguageCapsule,
// or a server-restored pick replayed into this browser). Lets auth_context tell
// "user actively chose X in this browser" apart from the detector's own
// auto-cached default, so a live choice is never silently clobbered by a stale
// profile value on the next /auth/me poll.
export const LANGUAGE_EXPLICIT_KEY = 'pw_lang_explicit';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      fr: { translation: fr },
      de: { translation: de },
      pt: { translation: pt },
      zh: { translation: zh },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    nonExplicitSupportedLngs: true, // 'zh-CN' -> 'zh'
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ['path', 'localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      lookupFromPathIndex: 0,
      caches: ['localStorage'],
    },
    react: { useSuspense: false },
  });

// ── Keep <html lang="xx"> in sync with the active locale ───────────────────
// This matters for:
//   • Screen-readers (announced language for AT)
//   • Search-engine crawlers that rely on the lang attribute
//   • AI answer engines (GEO) that use the attribute to classify content
i18n.on('languageChanged', (lng) => {
  const code = (lng || 'en').slice(0, 2);
  document.documentElement.lang = code;
});
// Apply immediately for the first render (before any event fires).
try {
  const initial =
    new URLSearchParams(window.location.search).get('lang') ||
    window.localStorage.getItem(LANGUAGE_STORAGE_KEY) ||
    (navigator.languages && navigator.languages[0]) ||
    navigator.language ||
    'en';
  document.documentElement.lang = initial.slice(0, 2);
} catch { /* private-mode / SSR */ }

export default i18n;
